import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

interface ShortcutCheatsheetProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutItem {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  group: string;
  items: ShortcutItem[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    group: 'Selection',
    items: [
      { keys: ['Click'], description: 'Select a cell' },
      { keys: ['Shift', 'Click'], description: 'Extend selection to clicked cell' },
      { keys: ['Click', 'Drag'], description: 'Select a range of cells' },
    ],
  },
  {
    group: 'Navigation',
    items: [
      { keys: ['↑ ↓ ← →'], description: 'Move selection (commits edit if active)' },
      { keys: ['Shift', '↑ ↓ ← →'], description: 'Extend selection (keep anchor, move focus)' },
      { keys: ['Tab'], description: 'Move to next editable cell in row' },
      { keys: ['Shift', 'Tab'], description: 'Move to previous editable cell in row' },
      { keys: ['Enter'], description: 'Commit edit and move down one row' },
      { keys: ['Shift', 'Enter'], description: 'Commit edit and move up one row' },
      { keys: ['0–9'], description: 'Type a digit to start editing and replace value' },
    ],
  },
  {
    group: 'Copy & Paste',
    items: [
      { keys: ['Ctrl', 'C'], description: 'Copy selected cell or range' },
      { keys: ['Ctrl', 'V'], description: 'Paste — fills entire selection if range is active' },
    ],
  },
  {
    group: 'Fill',
    items: [
      { keys: ['Ctrl', 'D'], description: 'Fill Down — repeat top-row value through selection' },
      { keys: ['Ctrl', 'R'], description: 'Fill Right — repeat left-column value through selection' },
    ],
  },
  {
    group: 'Editing',
    items: [
      { keys: ['F2'], description: 'Open value input for the current cell' },
      { keys: ['Ctrl', 'Shift', 'R'], description: 'Open repeat-values dialog (e.g. fill 5 columns with 100)' },
      { keys: ['Delete'], description: 'Clear selected cell or range (sets value to 0)' },
    ],
  },
  {
    group: 'Undo / Redo',
    items: [
      { keys: ['Ctrl', 'Z'], description: 'Undo last change (up to 50 steps)' },
      { keys: ['Ctrl', 'Y'], description: 'Redo' },
      { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo (alternative)' },
    ],
  },
];

const kbdSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 0.75,
  py: 0.2,
  border: '1px solid #c8c8c8',
  borderBottomWidth: '2px',
  borderRadius: '4px',
  bgcolor: '#f5f5f5',
  fontSize: '0.68rem',
  fontFamily: 'monospace',
  fontWeight: 700,
  color: '#333',
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
};

function KeyCombo({ keys }: { keys: string[] }) {
  return (
    <Stack direction='row' alignItems='center' spacing={0.4} flexShrink={0}>
      {keys.map((k, i) => (
        <React.Fragment key={k + i}>
          {i > 0 && (
            <Typography component='span' sx={{ fontSize: '0.65rem', color: '#aaa', lineHeight: 1 }}>
              +
            </Typography>
          )}
          <Box component='kbd' sx={kbdSx}>
            {k}
          </Box>
        </React.Fragment>
      ))}
    </Stack>
  );
}

export function ShortcutCheatsheet({ open, onClose }: ShortcutCheatsheetProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797' }}>
            Keyboard Shortcuts
          </Typography>
          <IconButton size='small' onClick={onClose}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 3 }}>
        <Stack spacing={2.5}>
          {SHORTCUT_GROUPS.map((group, gi) => (
            <React.Fragment key={group.group}>
              {gi > 0 && <Divider />}
              <Stack spacing={0.5}>
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 700,
                    color: '#42BB93',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    mb: 0.5,
                  }}
                >
                  {group.group}
                </Typography>
                {group.items.map((item) => (
                  <Stack
                    key={item.keys.join('+')}
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{
                      py: 0.6,
                      px: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                  >
                    <Typography variant='body2' sx={{ color: '#444', fontSize: '0.8rem', flex: 1, pr: 2 }}>
                      {item.description}
                    </Typography>
                    <KeyCombo keys={item.keys} />
                  </Stack>
                ))}
              </Stack>
            </React.Fragment>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
